/** @module csi/toolbar/MultiFilter */
define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/aspect",
	"dojo/store/Memory",
	"dojo/when",
	"dijit/_Container",
	"dijit/_TemplatedMixin",
	"dijit/_WidgetBase",
	"dijit/_WidgetsInTemplateMixin",
	"csi",
	"csi/_base/underscore",
	"csi/html",
	"csi/locale",
	"csi/state",
	"csi/view/_query/AdHocFilterContainer",
	"csi/widget/LanguageUtil",
	"./MultiFilterCondition",
	"dojo/text!./MultiFilter.html",

	"dijit/Tooltip",
	"dijit/form/Button",
	"csi/widget/CheckedMultiSelectDropdown",
	"csi/widget/DateRange",
	"csi/widget/FilteringSelect"
], function(declare, lang, aspect, Memory, when, _Container, _TemplatedMixin, _WidgetBase, _WidgetsInTemplateMixin, csi, _, html, locale, state, AdHocFilterContainer, LanguageUtil, MultiFilterCondition, template) {

	return declare([_WidgetBase, _Container, _TemplatedMixin, _WidgetsInTemplateMixin], {
		templateString: template,
		baseClass: "csiToolbarFilter",

		/** @property {AdHocFilterContainer} filterContainer - location to render currently applied filters */
		filterContainer: null,

		/** @property {string} [defaultAttribute] - attribute to select by default; if not specified, then default to first attribute */
		//defaultAttribute: "",

		// _QueryMixin: object holding query value and stores
		//queryMixin: null,

		// connection points
		//_attrNode: null,
		//_multiValueNode: null,
		//_timeValueNode: null,
		//_tooltipNode: null,

		// _WidgetBase: widget currently used to set attribute filter value, which is either _multiValueNode or _timeValueNode
		//_valueNode: null,

		// Boolean: flag to indicate if change to query was triggered internally or externally
		//_bInternalChange: false,

		// String: first attribute to select - needs to be in store
		//_firstId: null,

		// Object[]: listener handles
		//_handles: null,

		/**
		 * Create filter container to display ad-hoc filters
		 * @param {DOMNode} domNode - container for filters
		 */
		createFilterContainer: function(domNode) {
			var self = this;

			this.filterContainer = new AdHocFilterContainer({
				queryMixin: this.queryMixin,
				onClearFilters: function() {
					self._resetFilter();
				},
				onClickFilter: function(key) {
					self._attrNode.set("value", key);
				},
				onRemoveFilter: function(key) {
					self._changeFilter(key, []);
				}
			}).placeAt(domNode.firstChild, "after");
		},

		onChangeAttribute: function(/*String*/id) {
			// summary: extension point for change to selected attribute
		},

		onChange: function(/*Object*/value) {
			// summary: extension point for change to query
		},

		postCreate: function() {
			// summary: initialize widget
			this.inherited(arguments);
			this._tooltipNode.set("connectId", this._resetNode.domNode);
			this._tooltipNode.set("position", ["below-centered", "above-centered"]);
		},

		_changeFilter: function(/*String*/id, /*Any[]*/value) {
			// summary: change filter for given attribute
			if (this.queryMixin.setQueryValue(id, value)) {
				this.onChange(this.queryMixin.get("query"));
			}
		},

		getAttributeIds: function() {
			// summary: return list of attribute ids that can be filtered
			var attrStore = this._attrNode.get("store");
			return attrStore ? _.pluck(attrStore.query(), attrStore.idProperty) : [];
		},

		_onChangeAttribute: function(/*String*/id) {
			// summary: update valueNode with appropriate parameters (without triggering onChange) and refocus on textbox when attribute is selected
			var spec = this.queryMixin.getSpec(id);
			if (!spec) {
				return;
			}

			var w1 = spec.isDateAttribute() ? this._timeValueNode : this._multiValueNode;
			var w2 = spec.isDateAttribute() ? this._multiValueNode : this._timeValueNode;

			this._valueNode = w1;
			this._updateValueNode(this.queryMixin.getSpecValuesWidgetParams(id));

			// switch widget being displayed
			html.setVisible(w2, false);
			html.setVisible(w1, true);
			this._setDisabledAttr(false);

			// pass selected filter attribute change to filter container
			if (this.filterContainer) {
				this.filterContainer.selectAttribute(id);
			}

			this.onChangeAttribute(id);
		},

		_onChangeData: function() {
			// summary: update value store when view data changes; not necessary when filtering by time as it does not require a store
			var id = this._attrNode.get("value");
			if (id && (this._valueNode == this._multiValueNode)) {
				this._updateValueNode(this.queryMixin.getSpecValuesWidgetParams(id));
			}

			this._updateTooltipNode();
		},

		_onChangeValue: function(/*Any[]*/value) {
			// summary: trigger onChange event
			this._bInternalChange = true;
			var id = this._attrNode.get("value");
			this._changeFilter(id, value);
			// if we have bAutoCoplete enabled on value multiselect combo box, then we have to reset store for next use
			if (id && (this._valueNode == this._multiValueNode) && this._valueNode.store.bAutoComplete) {
				this._updateValueNode(this.queryMixin.getSpecValuesWidgetParams(id));
			}
			this._bInternalChange = false;
		},

		_onWatchQuery: function(/*String*/attr, /*Object*/oldVal, /*Object*/newVal) {
			// summary: update value with current value (without triggering onChange) and tooltip
			var id = this._attrNode.get("value");
			if (id && !this._bInternalChange) {
				this._updateValueNode(this.queryMixin.getSpecValuesWidgetParams(id));
			}

			this._updateTooltipNode();
		},

		_onWatchStore: function(/*String*/attr, /*Store*/oldVal, /*Store*/newVal) {
			// summary: when using a FilteringSelect widget, trigger filter when mouse clicks on entry or user hits enter
			// NOTE: image browser relies on this same object being used to store changes to filter values
			var store = newVal;

			when(store && store.query(), lang.hitch(this, function(results) {
				// remove any results that do not allow filtering
				results = _.filter(results, function(result) {
					return result.spec.canFilter();
				});

				if (results && results.length) {
					var id = this._attrNode.get("value");

					// augment with label property to support select widget
					_.each(results, function(item) {
						item.filterLabel = item.filterLabel || item.name;
					});

					// select first attribute as the default value
					if (this.defaultAttribute && store.get(this.defaultAttribute)) {
						this._firstId = this.defaultAttribute;
					} else {
						this._firstId = store.getIdentity(results[0]);
					}

					// sort attributes against language so they are alphabetical rather than in order of view
					if (LanguageUtil.isPinyinEnabled()) {
						results = csi.sort(results, item => LanguageUtil.getPinyin(item.name));
					} else {
						results = csi.sort(results, "name");
					}

					this._attrNode.set("store", new Memory({
						idProperty: store.idProperty,
						data: results
					}));

					if (store.get(id)) {
						this._onChangeAttribute(id);
					} else {
						this._attrNode.set("value", this._firstId);
					}

					this._updateTooltipNode();

					// NOTE: don't set disabled to false - let toolbar control enabling of widget when it is ready
				} else {
					this._updateValueNode({value: []});
					this._attrNode.set("store", null);
					this.set("disabled", true);
				}
			}));
		},

		_resetFilter: function() {
			// reset all data filters for attributes in store
			var qm = this.queryMixin;
			var bChange;

			_.each(this.getAttributeIds(), function(id) {
				if (qm.setQueryValue(id, null)) {
					bChange = true;
				}
			});

			if (bChange) {
				this.onChange(null);
			}

			if (this._attrNode.store) {
				this._attrNode.set("value", this._firstId);
			}
		},

		_setDisabledAttr: function(/*Boolean*/b) {
			// summary: set disabled status for all child widgets; disable all if no attribute store, or value node if it does not have a store
			if (!b && !this._attrNode.get("store")) {
				b = true;
			}

			this._set("disabled", b);
			this._attrNode.set("disabled", b);
			this._multiValueNode.set("disabled", b || !this._multiValueNode.get("store"));
			this._resetNode.set("disabled", b || _.isEmpty(this._getDynamicQuery()));
			this._timeValueNode.set("disabled", b);
		},

		_setQueryMixinAttr: function(/*Object*/queryMixin) {
			// summary: update attribute store and watch for changes to attribute store and query
			this._set("queryMixin", queryMixin);

			_.each(this._handles, function(h) {h.remove();});
			delete this._handles;

			if (queryMixin) {
				this._handles = this.own(
					aspect.after(queryMixin, "onChangeData", lang.hitch(this, this._onChangeData)),
					queryMixin.watch("querySpecStore", lang.hitch(this, this._onWatchStore)),
					queryMixin.watch("query", lang.hitch(this, this._onWatchQuery))
				);

				this._onWatchStore(null, null, queryMixin.get("querySpecStore"));
			} else {
				this.set("disabled", true);
			}

			if (this.filterContainer) {
				this.filterContainer.set("queryMixin", queryMixin);
			}
		},

		_getDynamicQuery: function() {
			// summary: return query
			var query = this.queryMixin && this.queryMixin.get("query");
			return _.pick(query || {}, this.getAttributeIds());
		},

		_updateTooltipNode: function(/*Tooltip?*/tooltip, /*String?*/id) {
			// summary: make sure to only show values in query that are controlled through this widget;
			// we need to filter out fixed filters such as those imposed by view context
			tooltip = tooltip || this._tooltipNode;
			var domNode = tooltip.domNode;
			var qm = this.queryMixin;
			var q = this._getDynamicQuery();

			html.clear(domNode, true);

			if (id) {
				// populate tooltip with single filter condition if filtering by more than one value; not necessary for time attributes which use single filter range
				var spec = qm.getSpec(id);
				var valueLabels = qm.getQueryValueAsLabel(id, false);

				if (valueLabels.length > 1) {
					new MultiFilterCondition({
						label: spec?.name,
						value: valueLabels
					}).placeAt(domNode);
				}
			} else if (!_.isEmpty(q)) {
				// FIX: in new design, may want to use old tooltip, so keep it around - locale.resource("MultiFilterTooltipTitle")
				html.create("div", {
					"class": "csi-filter-condition-title",
					innerText: locale.resource("ViewFilterReset")
				}, domNode);

				_.each(q, function(value, key) {
					var v = qm.getQueryValueAsLabel(key, false);
					if (v != null) {
						new MultiFilterCondition({
							label: qm.getSpec(key).name,
							value: v
						}).placeAt(domNode);
					}
				}, this);
			}
		},

		_updateValueNode: function(/*Object*/params, /*_Widget*/w) {
			// summary: update valueNode parameters
			if (!params) return;

			w = w || this._valueNode;
			w._onChangeActive = false;

			if (w.resetSelected) {
				// reset value to empty before changing store - want to make sure that store and value do not conflict;
				// after store is set, then set new value
				var value = params.value;
				delete params.value;
				// reset the previous values to null before applying the params to multiselect widget
				w.resetSelected([]);
				w.set(params);

				if (value && value.length) {
					if (_.every(value, function(v) {return !!params.store.getFromMemory(v);})) {
						w.resetSelected([].concat(value) || []);
					} else {
						console.assert(false, "Initializing filter widget with values not in store.");
					}
				}
			} else {
				w.set(params);
			}

			w._onChangeActive = true;
		}
	});
});
